/*
 * Choose-your-gift picker (Oct 23 2026). Replaces free-gift-auto-add.js.
 *
 * Each tier reached (₹400 / ₹600 / ₹800 on the subtotal of non-gift lines)
 * unlocks one pick. A pick is a cart line with hidden properties
 *   _stp_gift       = pct5 | tote | io10 | hs10 | pouchy
 *   _stp_gift_slot  = 1 | 2 | 3   (keeps same-option picks on separate lines)
 *   _stp_gift_label = shown in the cart status line
 * The rewards-gift-discount Function makes earned picks free and turns each
 * pct5 pick into 5% off the customer's own lines.
 *
 * This script:
 *  - opens the picker the first time a new tier is reached (once per tier
 *    per session; closing it is fine, the cart status line reopens it)
 *  - removes picks the cart no longer earns (highest slot first)
 *  - removes leftover auto-added free gifts from the old flow
 */
(function () {
  var SOURCE = 'stp-gift-picker';
  var SEEN_KEY = 'stp_gift_seen_tier';
  var CONFIG = null;
  var lastCart = null;
  var syncing = false;
  var resyncQueued = false;

  function loadConfig() {
    var el = document.getElementById('StpGiftPickerConfig');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function readSeen() {
    try {
      return parseInt(sessionStorage.getItem(SEEN_KEY) || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  function writeSeen(n) {
    try {
      sessionStorage.setItem(SEEN_KEY, String(n));
    } catch (e) {}
  }

  function isPick(item) {
    return Boolean(item.properties && item.properties._stp_gift);
  }

  function analyse(cart) {
    var own = 0;
    var picks = [];
    var legacy = [];
    var optionVariantIds = CONFIG.options.map(function (o) {
      return o.variantId;
    });
    cart.items.forEach(function (item) {
      if (isPick(item)) {
        picks.push({
          key: item.key,
          gift: item.properties._stp_gift,
          slot: parseInt(item.properties._stp_gift_slot || '9', 10),
          quantity: item.quantity,
        });
        return;
      }
      // Old auto-added free gift (no picker properties, discounted to ₹0).
      if (
        optionVariantIds.indexOf(item.variant_id) !== -1 &&
        item.final_line_price === 0 &&
        item.original_line_price > 0
      ) {
        legacy.push(item.key);
        return;
      }
      own += item.original_line_price;
    });
    picks.sort(function (a, b) {
      return a.slot - b.slot;
    });
    var earned = CONFIG.thresholds.filter(function (t) {
      return own >= t;
    }).length;
    var pickedUnits = picks.reduce(function (sum, p) {
      return sum + p.quantity;
    }, 0);
    return { own: own, picks: picks, legacy: legacy, earned: earned, pickedUnits: pickedUnits };
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  function announce() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: SOURCE } }));
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // ---- Sync -------------------------------------------------------------

  function sync() {
    if (!CONFIG) return;
    if (syncing) {
      resyncQueued = true;
      return;
    }
    syncing = true;

    getCart()
      .then(function (cart) {
        var a = analyse(cart);
        var removals = a.legacy.slice();

        // Drop picks beyond what's earned, highest slot first.
        var excess = a.pickedUnits - a.earned;
        for (var i = a.picks.length - 1; i >= 0 && excess > 0; i--) {
          var p = a.picks[i];
          var drop = Math.min(p.quantity, excess);
          excess -= drop;
          removals.push({ key: p.key, quantity: p.quantity - drop });
        }

        var chain = Promise.resolve();
        removals.forEach(function (r) {
          var body = typeof r === 'string' ? { id: r, quantity: 0 } : { id: r.key, quantity: r.quantity };
          chain = chain.then(function () {
            return post('/cart/change.js', body);
          });
        });

        return chain.then(function () {
          return removals.length ? getCart().then(function (c) { return { cart: c, changed: true }; }) : { cart: cart, changed: false };
        });
      })
      .then(function (res) {
        lastCart = res.cart;
        var a = analyse(res.cart);
        if (res.changed) announce();

        var seen = readSeen();
        if (a.earned > seen && a.pickedUnits < a.earned) {
          writeSeen(a.earned);
          openPicker();
        } else if (a.earned < seen) {
          writeSeen(a.earned);
        }
      })
      .catch(function () {})
      .then(function () {
        syncing = false;
        if (resyncQueued) {
          resyncQueued = false;
          sync();
        }
      });
  }

  // ---- Picker -----------------------------------------------------------
  // Taps only change a draft; nothing touches the cart until the bottom
  // button ("Add free gift" / "Save changes") is pressed. Closing discards it.

  var overlay = null;
  var activeSlot = null;
  var draft = {}; // slot -> option key
  var saved = {}; // slot -> option key, as in the cart when the picker opened

  function optionByKey(key) {
    return CONFIG.options.find(function (o) {
      return o.key === key;
    });
  }

  function closePicker() {
    if (!overlay) return;
    var el = overlay;
    overlay = null;
    el.classList.remove('stp-gp--visible');
    setTimeout(function () {
      el.remove();
    }, 220);
  }

  function slotLabel(slot) {
    return 'Gift ' + slot;
  }

  function savedFromCart() {
    var out = {};
    analyse(lastCart).picks.forEach(function (p) {
      out[p.slot] = p.gift;
    });
    return out;
  }

  function firstEmptySlot(earned) {
    for (var s = 1; s <= earned; s++) if (!draft[s]) return s;
    return null;
  }

  function changedSlots(earned) {
    var out = [];
    for (var s = 1; s <= earned; s++) if (draft[s] && draft[s] !== saved[s]) out.push(s);
    return out;
  }

  function render() {
    if (!overlay || !lastCart) return;
    var a = analyse(lastCart);
    if (!activeSlot || activeSlot > a.earned) activeSlot = firstEmptySlot(a.earned) || (a.earned ? 1 : null);

    var slotsHtml = '';
    for (var n = 1; n <= a.earned; n++) {
      var o = draft[n] ? optionByKey(draft[n]) : null;
      slotsHtml +=
        '<button type="button" class="stp-gp__slot' +
        (n === activeSlot ? ' stp-gp__slot--active' : '') +
        (draft[n] && draft[n] !== saved[n] ? ' stp-gp__slot--changed' : '') +
        '" data-gp-slot="' + n + '">' +
        '<span class="stp-gp__slot-name">' + slotLabel(n) + '</span>' +
        '<span class="stp-gp__slot-value">' + (o ? o.label : 'Not chosen') + '</span>' +
        '</button>';
    }

    var optionsHtml = CONFIG.options
      .map(function (o) {
        var current = activeSlot && draft[activeSlot] === o.key;
        var elsewhere = [];
        for (var s = 1; s <= a.earned; s++) if (s !== activeSlot && draft[s] === o.key) elsewhere.push(s);
        var media = o.image
          ? '<img src="' + o.image + '" alt="" loading="lazy">'
          : '<span class="stp-gp__emoji">' + (o.emoji || '🎁') + '</span>';
        var tag = current
          ? '✓ Selected'
          : elsewhere.length
            ? o.key === 'pct5'
              ? 'Picked for Gift ' + elsewhere.join(', ') + '. Again = ' + (elsewhere.length + 1) * 5 + '% off'
              : '✓ Gift ' + elsewhere.join(', ') + ', pick again?'
            : '';
        return (
          '<button type="button" class="stp-gp__option' + (current ? ' stp-gp__option--current' : '') + '" data-gp-pick="' + o.key + '"' + (activeSlot ? '' : ' disabled') + ' aria-pressed="' + (current ? 'true' : 'false') + '">' +
          '<span class="stp-gp__media">' + media + '</span>' +
          '<span class="stp-gp__name">' + o.label + '</span>' +
          '<span class="stp-gp__sub">' + o.sub + '</span>' +
          (tag ? '<span class="stp-gp__tag">' + tag + '</span>' : '') +
          '</button>'
        );
      })
      .join('');

    var hasSaved = Object.keys(saved).length > 0;
    var title = a.earned === 0 ? 'Free gifts unlock at ₹' + Math.round(CONFIG.thresholds[0] / 100) : hasSaved ? 'Your FREE gifts' : a.earned > 1 ? 'Choose your FREE gifts' : 'Choose your FREE gift';
    var sub =
      a.earned === 0
        ? 'Add a little more to your cart to pick a gift.'
        : 'Your order is above ₹' + Math.round(CONFIG.thresholds[a.earned - 1] / 100) + ', so you get ' + a.earned + ' FREE gift' + (a.earned > 1 ? 's' : '') + '. ' +
          (a.earned > 1 ? 'Now choosing ' + slotLabel(activeSlot) + '. Tap a gift above to switch.' : 'Pick it below.');

    // Bottom action bar.
    var changed = changedSlots(a.earned);
    var newOnes = changed.filter(function (s) { return !saved[s]; }).length;
    var ctaText;
    if (a.earned === 0) ctaText = 'Keep shopping';
    else if (!changed.length) ctaText = hasSaved ? 'Done' : 'Select a gift';
    else if (newOnes < changed.length) ctaText = 'Save changes';
    else ctaText = newOnes > 1 ? 'Add ' + newOnes + ' free gifts' : 'Add free gift';
    var ctaDisabled = a.earned > 0 && !changed.length && !hasSaved;
    var left = 0;
    for (var t = 1; t <= a.earned; t++) if (!draft[t]) left++;
    var note = left && changed.length ? left + ' more gift' + (left > 1 ? 's' : '') + ' to choose, or add them later' : '';

    overlay.querySelector('.stp-gp__title').textContent = title;
    overlay.querySelector('.stp-gp__sub-title').textContent = sub;
    overlay.querySelector('.stp-gp__slots').innerHTML = slotsHtml;
    overlay.querySelector('.stp-gp__options').innerHTML = optionsHtml;
    overlay.querySelector('.stp-gp__note').textContent = note;
    var cta = overlay.querySelector('.stp-gp__cta');
    cta.textContent = ctaText;
    cta.disabled = ctaDisabled;
    cta.setAttribute('data-gp-mode', changed.length ? 'save' : 'close');
  }

  function openPicker() {
    if (!CONFIG) return;
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'stp-gp';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Choose your free gift');
      overlay.innerHTML =
        '<div class="stp-gp__backdrop" data-gp-close></div>' +
        '<div class="stp-gp__card">' +
        '<div class="stp-gp__body">' +
        '<button type="button" class="stp-gp__close" data-gp-close aria-label="Close">&times;</button>' +
        '<p class="stp-gp__title"></p>' +
        '<p class="stp-gp__sub-title"></p>' +
        '<div class="stp-gp__slots"></div>' +
        '<div class="stp-gp__options"></div>' +
        '</div>' +
        '<div class="stp-gp__footer">' +
        '<p class="stp-gp__note"></p>' +
        '<button type="button" class="stp-gp__cta"></button>' +
        '</div>' +
        '</div>';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', onOverlayClick);
      requestAnimationFrame(function () {
        if (overlay) overlay.classList.add('stp-gp--visible');
      });
    }
    getCart().then(function (c) {
      lastCart = c;
      saved = savedFromCart();
      draft = Object.assign({}, saved);
      activeSlot = null;
      render();
    });
  }

  function onOverlayClick(e) {
    if (e.target.closest('[data-gp-close]')) {
      closePicker();
      return;
    }
    if (overlay.classList.contains('stp-gp--busy')) return;
    var slotBtn = e.target.closest('[data-gp-slot]');
    if (slotBtn) {
      activeSlot = parseInt(slotBtn.getAttribute('data-gp-slot'), 10);
      render();
      return;
    }
    var pickBtn = e.target.closest('[data-gp-pick]');
    if (pickBtn && !pickBtn.disabled && activeSlot) {
      draft[activeSlot] = pickBtn.getAttribute('data-gp-pick');
      // New pick: move on to the next empty slot. Editing: stay put.
      var earned = analyse(lastCart).earned;
      if (!saved[activeSlot]) activeSlot = firstEmptySlot(earned) || activeSlot;
      render();
      return;
    }
    var cta = e.target.closest('.stp-gp__cta');
    if (cta && !cta.disabled) {
      if (cta.getAttribute('data-gp-mode') === 'save') save(cta);
      else closePicker();
    }
  }

  function save(cta) {
    var a = analyse(lastCart);
    var slots = changedSlots(a.earned);
    if (!slots.length) return closePicker();
    var bySlot = {};
    a.picks.forEach(function (p) {
      bySlot[p.slot] = p;
    });
    var updates = {};
    slots.forEach(function (s) {
      if (bySlot[s]) updates[bySlot[s].key] = 0;
    });
    var items = slots.map(function (s) {
      var option = optionByKey(draft[s]);
      return {
        id: option.variantId,
        quantity: 1,
        properties: { _stp_gift: option.key, _stp_gift_slot: String(s), _stp_gift_label: option.label },
      };
    });

    overlay.classList.add('stp-gp--busy');
    var label = cta.textContent;
    cta.textContent = 'Saving…';
    cta.disabled = true;

    (Object.keys(updates).length ? post('/cart/update.js', { updates: updates }) : Promise.resolve())
      .then(function () {
        return post('/cart/add.js', { items: items });
      })
      .then(getCart)
      .then(function (cart) {
        lastCart = cart;
        announce();
        closePicker();
      })
      .catch(function () {
        if (!overlay) return;
        overlay.classList.remove('stp-gp--busy');
        cta.textContent = label;
        cta.disabled = false;
        overlay.querySelector('.stp-gp__note').textContent = 'Could not save. Please try again.';
      });
  }

  // ---- Wiring -----------------------------------------------------------

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-gift-picker-open]');
    if (!trigger) return;
    e.preventDefault();
    openPicker();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePicker();
  });

  document.addEventListener('DOMContentLoaded', function () {
    CONFIG = loadConfig();
    if (!CONFIG) return;
    sync();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === SOURCE) return;
        sync();
      });
    }
    document.addEventListener('cart:updated', function (event) {
      if (event && event.detail && event.detail.source === SOURCE) return;
      sync();
    });
  });

  window.StpGiftPicker = { open: openPicker };
})();
